import printJS from 'print-js'

import type {
  TipoRepresentacionGrafica,
  UrlsRepresentacionGraficaProps,
} from '../../../../base/interfaces/base'
import { IMPRESION_FORMATO, ImpresionFormatoProps } from '../interfaces/impresora.ts'

/** PDF a imprimir: Blob generado en el frontend o URL (representación gráfica del backend o blob:) */
export type PdfFuenteProps = Blob | string

/** Campos imprimibles de la representación gráfica del backend (`sin` y `xml` no se imprimen) */
export type CampoRepresentacionImpresion = keyof Pick<
  UrlsRepresentacionGraficaProps,
  'pdf' | 'rollo' | 'rollo2' | 'rolloResumen' | 'rolloReducido'
>

/**
 * URLs imprimibles de `UrlsRepresentacionGraficaProps`, todas opcionales: no todos los documentos
 * traen todos los formatos (solo las facturas tienen rollo2, resumen y reducido).
 */
export type RepresentacionGraficaUrls = {
  [K in CampoRepresentacionImpresion]?: UrlsRepresentacionGraficaProps[K] | null
}

/**
 * Única equivalencia entre el formato de impresión (lo que se guarda en la asignación) y el campo de
 * la representación gráfica del backend. Agregar un formato = una línea aquí.
 */
export const IMPRESION_FORMATO_REPRESENTACION = {
  [IMPRESION_FORMATO.PDF]: 'pdf',
  [IMPRESION_FORMATO.ROLLO]: 'rollo',
  [IMPRESION_FORMATO.ROLLO2]: 'rollo2',
  [IMPRESION_FORMATO.ROLLO_RESUMEN]: 'rolloResumen',
  [IMPRESION_FORMATO.RO_REDUCIDO]: 'rolloReducido',
} as const satisfies Record<ImpresionFormatoProps, CampoRepresentacionImpresion>

const FORMATOS = Object.keys(IMPRESION_FORMATO_REPRESENTACION) as ImpresionFormatoProps[]

/** Formatos de papel térmico (rollo) */
export const esFormatoRollo = (formato?: ImpresionFormatoProps | null) => formato !== IMPRESION_FORMATO.PDF

/** Formato equivalente a `user.tipoRepresentacionGrafica`, respaldo cuando no hay asignación */
export const formatoDesdeTipoRepresentacion = (
  tipo?: TipoRepresentacionGrafica | string | null,
): ImpresionFormatoProps =>
  // sin, xml o sin preferencia → rollo
  FORMATOS.find((f) => IMPRESION_FORMATO_REPRESENTACION[f] === tipo) ?? IMPRESION_FORMATO.ROLLO

/**
 * URL de la representación gráfica en el formato pedido, con respaldo al rollo (o al PDF).
 * Los formatos opcionales (rollo2, rolloResumen, rolloReducido) usan el rollo si no vienen.
 */
export const urlRepresentacionPorFormato = (
  rg: RepresentacionGraficaUrls | null | undefined,
  formato: ImpresionFormatoProps,
): string => {
  if (!rg) throw new Error('El documento no tiene representación gráfica')
  const url = rg[IMPRESION_FORMATO_REPRESENTACION[formato]] || rg.rollo || rg.pdf
  if (!url) throw new Error('El documento no tiene representación gráfica disponible')
  return url
}

/** Las URLs blob:/data: solo existen en este navegador; isi-print no puede descargarlas */
export const esUrlLocal = (url: string) => url.startsWith('blob:') || url.startsWith('data:')

/** Obtiene el PDF como Blob (descarga la URL si hace falta) */
export const pdfABlob = async (pdf: PdfFuenteProps): Promise<Blob> => {
  if (pdf instanceof Blob) return pdf
  const res = await fetch(pdf)
  if (!res.ok) throw new Error(`No se pudo obtener el PDF (${res.status})`)
  return res.blob()
}

const ESTILO_ROLLO = '@media print { @page { size: 100%; margin: 0mm; } body { width: 100%; } }'

/**
 * Abre el diálogo de impresión del navegador / sistema operativo.
 * Si no se puede (CORS, bloqueo), abre el PDF en otra pestaña.
 */
export const imprimirEnNavegador = async (pdf: PdfFuenteProps, formato?: ImpresionFormatoProps | null) => {
  let blob: Blob
  try {
    blob = await pdfABlob(pdf)
  } catch {
    if (typeof pdf === 'string') window.open(pdf, '_blank', 'noopener,noreferrer')
    return
  }
  const url = URL.createObjectURL(blob)
  printJS({
    printable: url,
    type: 'pdf',
    ...(esFormatoRollo(formato) ? { style: ESTILO_ROLLO } : {}),
    onPrintDialogClose: () => URL.revokeObjectURL(url),
    onError: () => window.open(url, '_blank', 'noopener,noreferrer'),
  })
}

/** URL descargable desde fuera de este navegador (WhatsApp, correo, compartir) */
export const esUrlPublica = (u: unknown): u is string => typeof u === 'string' && /^https?:\/\//i.test(u)

/**
 * URL pública del documento: la del PDF actual si lo es; si es un PDF generado en el navegador, la
 * del formato carta (`obtenerPdf('PDF')`) cuando existe. null si solo existe en este navegador.
 */
export const obtenerUrlPublicaPdf = async (
  pdf: PdfFuenteProps | null,
  obtenerPdf?: (formato: ImpresionFormatoProps) => Promise<PdfFuenteProps> | PdfFuenteProps,
): Promise<string | null> => {
  if (esUrlPublica(pdf)) return pdf
  if (!obtenerPdf) return null
  try {
    const carta = await obtenerPdf(IMPRESION_FORMATO.PDF)
    return esUrlPublica(carta) ? carta : null
  } catch {
    return null
  }
}

const nombrePdf = (filename?: string) => {
  const base = (filename || 'documento').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'documento'
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`
}

/**
 * El navegador permite compartir archivos (hoja de compartir del sistema en teléfonos: WhatsApp,
 * correo, AirPrint o apps de impresión de Android).
 */
export const puedeCompartirArchivos = (): boolean => {
  try {
    const prueba = new File([new Blob(['%PDF'], { type: 'application/pdf' })], 'prueba.pdf', {
      type: 'application/pdf',
    })
    return typeof navigator !== 'undefined' && !!navigator.canShare && navigator.canShare({ files: [prueba] })
  } catch {
    return false
  }
}

/**
 * Comparte el PDF con la hoja de compartir del sistema. Si no se puede descargar el archivo (CORS),
 * comparte el enlace. Devuelve false si el usuario canceló o el navegador no permite compartir.
 */
export const compartirPdf = async (
  pdf: PdfFuenteProps,
  titulo: string,
  filename?: string,
): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.share) return false
  try {
    const blob = await pdfABlob(pdf)
    const file = new File([blob], nombrePdf(filename), { type: 'application/pdf' })
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: titulo })
      return true
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return false
    // No se pudo obtener el archivo: se intenta con el enlace
  }
  if (!esUrlPublica(pdf)) return false
  try {
    await navigator.share({ url: pdf, title: titulo })
    return true
  } catch {
    return false
  }
}

/** Descarga el PDF; si no se puede (CORS), lo abre en otra pestaña */
export const descargarPdf = async (pdf: PdfFuenteProps, filename?: string) => {
  let url: string
  let revocar = false
  try {
    url = URL.createObjectURL(await pdfABlob(pdf))
    revocar = true
  } catch {
    if (typeof pdf !== 'string') throw new Error('No se pudo descargar el documento')
    url = pdf
  }
  const a = document.createElement('a')
  a.href = url
  a.download = nombrePdf(filename)
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  if (revocar) setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
